/**
 * Validates email format against a standard regex pattern
 * @param email - The email string to validate
 * @returns true if email is valid, false otherwise
 */
export function isValidEmail(email: unknown): email is string {
    if (typeof email !== 'string') return false;
    const cleanedEmail = email.replace(/\s+/g, '');
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(cleanedEmail);
}

/**
 * Mimics the PL/pgSQL function `normalize_email` which is a generated index expression, 
 * so the result of this local function should match the generated index expression.
 * @param email - The email string to normalize
 * @returns Normalized email string (lowercase, no spaces)
 * @throws Error if email is not a string
 */
export function normalizeEmail(email: string): string {
    if (typeof email !== 'string') {
      throw new Error('Email must be a string.');
    }
    return email.replace(/\s+/g, '').toLowerCase();
}

/**
 * Mimics the PL/pgSQL function `clean_phone`. Which is a generated index expression, 
 * so the result of this local function should match the generated index expression.
 * @param phone - The phone number string to normalize
 * @returns Normalized phone string or null if empty/invalid
 * @throws Error for invalid phone number formats
 */
export function normalizePhone(phone: string | null | undefined): string | null {
    if (!phone || phone.trim() === '') {
      return null;
    }
  
    // Remove ".0" at the end and any non-digit characters.
    let cleaned = phone.replace(/\.0$/g, '');
    cleaned = cleaned.replace(/\D/g, '');
  
    if (cleaned.length === 0) {
      throw new Error(
        `Invalid phone: "${phone}". After removing non-digit characters the phone is empty.`
      );
    }
  
    // If the phone is 10 digits long, add the country code "1"
    if (cleaned.length === 10) {
      cleaned = '1' + cleaned;
    } else if (cleaned.length === 11) {
      // 11-digit phones must start with "1"
      if (!cleaned.startsWith('1')) {
        throw new Error(
          `Invalid phone: "${phone}". 11-digit phone numbers must start with a "1".`
        );
      }
    } else {
      throw new Error(
        `Invalid phone: "${phone}". Must be 10 or 11 digits (including country code).`
      );
    }
  
    // Verify that the phone contains only digits.
    if (!/^\d+$/.test(cleaned)) {
      throw new Error(`Invalid phone: "${phone}". Must contain only digits.`);
    }
  
    return cleaned;
}

/**
 * Validate phone locally.
 * Mimics the PL/pgSQL function `is_valid_phone`
 * @param phone - The phone number to validate
 * @returns true if phone is valid, false otherwise
 */
export function isValidPhone(phone: string | null | undefined): boolean {
    try {
      const result = normalizePhone(phone);
      return result !== null;
    } catch {
      return false;
    }
}

/**
 * Get validation error message for invalid email
 * @param email - The email to validate
 * @returns Error message string or null if valid/empty
 */
export function getEmailValidationError(email: string | null | undefined): string | null {
    if (!email || email.trim() === '') {
        return null; // Empty is okay
    }
    if (!isValidEmail(email)) {
        return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return null;
}

/**
 * Get validation error message for invalid phone
 * @param phone - The phone number to validate
 * @returns Error message string or null if valid/empty
 */
export function getPhoneValidationError(phone: string | null | undefined): string | null {
    if (!phone || phone.trim() === '') {
        return null; // Empty is okay
    }
    try {
        normalizePhone(phone);
        return null; // Valid
    } catch (error) {
        return (error as Error).message;
    }
}
